import React, {Component} from 'react';
import {any, string, shape, arrayOf, func, bool, oneOf} from 'prop-types';
import {prop, assoc, propEq} from 'ramda';

import RadioButton from '../../forms/RadioButton';
import Checkbox from '../../forms/Checkbox';
import Select from '../../forms/Select';
import Option from '../../forms/Option';

import styles from './FilterSet.css';

export const applyFilterSelection = ({name, isSelected, filters}) => {
  const filterIndex = filters.findIndex(propEq('name', name));
  const current = filters[filterIndex];
  const isUnchanged = current.selected === isSelected;
  // There should never be less than one active filter
  const canDeselect = fs => !isSelected && fs.filter(f => f.selected).length === 1;

  if (isUnchanged || canDeselect(filters)) {
    return filters;
  }

  const newFilters = Array.from(filters);
  newFilters[filterIndex] = {
    ...newFilters[filterIndex],
    selected: isSelected,
  };

  const unselect = assoc('selected', false);
  const isExclusive = prop('exclusive');

  //:: filter -> Boolean
  const isAppliedFilterExclusive = isExclusive(current);
  const shouldUnselectFilter = filter => filter.name !== name && (isAppliedFilterExclusive || isExclusive(filter));
  const updateFilterSelection = filter => shouldUnselectFilter(filter) ? unselect(filter) : filter;
  return newFilters.map(updateFilterSelection);
};

export const filterListShape = arrayOf(
  shape({
    name: string.isRequired,
    text: string.isRequired,
    value: any,
    selected: bool,
    exclusive: bool,
  }),
);

export class FilterSet extends Component {
  static propTypes = {
    groupName: string.isRequired,
    filterType: oneOf(['radio', 'checkbox']).isRequired,
    filters: filterListShape.isRequired,
    onFilterChange: func.isRequired,
  };

  constructor(props) {
    super(props);
    this.renderFilter = this.renderFilter.bind(this);
    this.onBroadcastChange = this.onBroadcastChange.bind(this);
  }
  render() {
    const {groupName, filters} = this.props;
    const broadcastFilter = filters.find(f => f.name === 'broadcast');
    const isBroadcastModeEnabled = broadcastFilter && broadcastFilter.selected;

    return (
      <div className={isBroadcastModeEnabled ? styles.extended : null}>
        <h6 className={styles.header}>{groupName}</h6>
        <ul>
          {filters.map(this.renderFilter)}
        </ul>
        {isBroadcastModeEnabled &&
          <Select
            className={styles.broadcastMenu}
            onChange={this.onBroadcastChange}
            defaultValue={broadcastFilter.channel}
          >
            {broadcastFilter.channels.map((channel, index) => (
              <Option key={index} label={channel.name} value={channel.value} />
            ))}
          </Select>}
      </div>
    );
  }
  renderFilter({name, text, selected, disabled}) {
    const {filterType} = this.props;
    const filterProps = {
      name: this.props.groupName,
      checked: !!selected,
      disabled: !!disabled,
      label: text,
    };

    return (
      <li key={name} className={styles.filter}>
        {filterType === 'radio'
          ? <RadioButton
            {...filterProps}
            onChange={({target}) => this.onRadioChange({name, isSelected: target.checked})}
            />
          : <Checkbox
            {...filterProps}
            theme="alternate"
            onChange={isSelected => this.onCheckboxChange({name, isSelected})}
            />}
      </li>
    );
  }
  onRadioChange({name}) {
    const {filters} = this.props;
    const matchesName = propEq('name', name);
    const setSelectedFilter = filter => assoc('selected', matchesName(filter), filter);
    const newFilters = filters.map(setSelectedFilter);

    this.props.onFilterChange(newFilters);
  }
  onCheckboxChange({name, isSelected}) {
    const {filters} = this.props;
    const newFilters = applyFilterSelection({name, isSelected, filters});

    if (newFilters !== filters) {
      this.props.onFilterChange(newFilters);
    }
  }
  onBroadcastChange({target}) {
    const {filters} = this.props;
    const broadcastFilterIndex = filters.findIndex(f => f.name === 'broadcast');
    const newBroadcastFilter = {
      ...filters[broadcastFilterIndex],
      channel: target.value,
    };

    const newFilters = [...filters];
    newFilters[broadcastFilterIndex] = newBroadcastFilter;

    this.props.onFilterChange(newFilters);
  }
}

export default FilterSet;
